import { Fragment } from 'react';
import { Link, useLocation } from 'react-router';
import type { AppRole } from '@infras/shared-types';
import { useCrumbTail } from '../lib/crumb';
import { docTrail } from '../lib/docs';
import { ASTRYX_TRIAL } from '../lib/trial';
// The scanner-freshness pill used to live here. Removed on request: it put a
// scanner's failure in the chrome of every page, where it read as an alarm about
// the page you were on. Scheduler still owns that signal (and keeps its dot in the
// sidebar), which is where someone goes to act on it.
import { GlobalSearchBar } from './GlobalSearchBar';
import { AccountMenu } from './AccountMenu';
import { Logo } from './Logo';

/**
 * Route → breadcrumb. Two levels: the nav GROUP, then the page. Longest prefix
 * wins, so `/repos/123` inherits `['Inventory','Repos']` and the entity name is
 * appended from the crumb context (see `lib/crumb`).
 *
 * Docs pages go deeper: under `Docs & MCP` come the parent section, if the page
 * is a subpage, and then the page itself (`docTrail` in `lib/docs`).
 *
 * Mirrors the sidebar's grouping on purpose — if a page's crumb group and its
 * nav group disagree, one of the two is wrong.
 */
const CRUMBS: [string, [string, string]][] = [
  // Search spans every doc type, but everything it returns lives under Inventory,
  // so it keeps that group rather than inventing a sixth.
  ['/overview', ['Inventory', 'Overview']],
  ['/search', ['Inventory', 'Search']],
  ['/repos', ['Inventory', 'Repos']],
  ['/clusters', ['Inventory', 'Clusters']],
  // Inherited fix, not part of this merge: `/alerts` shipped a route with no crumb,
  // so it rendered "Inventory / Overview" and `check-crumbs` — the guard that exists
  // for exactly this — has been failing on the base since. Group copied from its own
  // nav item (AppShell, Inventory group).
  ['/alerts', ['Inventory', 'Alerts']],
  ['/secrets', ['Inventory', 'Secrets']],
  ['/graph', ['Inventory', 'Repos']],
  ['/scheduler', ['Operations', 'Scheduler']],
  ['/activity', ['Operations', 'Activity']],
  ['/costs/cdn', ['Operations', 'CDN cost']],
  ['/incident', ['Operations', 'Notify owners']],
  // `/requests/*`, `/access/*` and `/changes/*` are redirects into `/tasks/*` (App.tsx).
  // A task's own page appends "#id" through the crumb tail: "Tasks / #42".
  ['/tasks', ['Tasks', 'Tasks']],
  ['/tasks/new', ['Tasks', 'New task']],
  ['/tasks/board', ['Tasks', 'Board']],
  // One key covers every drawer route by longest-prefix match, exactly as `/agents`
  // used to cover `/agents/commands/:id`.
  ['/cluster-access', ['Operations', 'Cluster access']],
  ['/catalog', ['Reference', 'Deploy catalog']],
  ['/error-codes', ['Reference', 'Error dictionary']],
  ['/docs', ['Reference', 'Docs & MCP']],
  ['/settings/team', ['Administration', 'Users & roles']],
  // Same short name as the sidebar item (the page's H2 says "Default access assignees").
  ['/settings/reviewers', ['Administration', 'Access assignees']],
  ['/settings/access-assignees', ['Administration', 'Access assignees']],
  ['/settings/questions', ['Administration', 'Access questions']],
  ['/settings/labels', ['Administration', 'Labels']],
  ['/settings/profile', ['Administration', 'Account']],
  ['/settings', ['Administration', 'Account']],
  ['/vpn/peers', ['Network', 'Peers']],
  // Astryx trial — QA and local builds only (lib/trial.ts).
  ...(ASTRYX_TRIAL ? ([['/vpn/peers-astryx', ['Network', 'Peers (Astryx)']]] satisfies [string, [string, string]][]) : []),
  ['/vpn/subnets', ['Network', 'Subnets']],
  ['/vpn/connection-audit', ['Network', 'Connection audit']],
];

function crumbFor(pathname: string): [string, string] {
  if (pathname === '/') return ['Inventory', 'Overview'];
  // longest prefix first so /settings/team beats /settings
  const hit = [...CRUMBS]
    .sort((a, b) => b[0].length - a[0].length)
    .find(([p]) => pathname === p || pathname.startsWith(p + '/'));
  return hit ? hit[1] : ['Inventory', 'Overview'];
}

export function TopBar({
  email,
  role,
  onLogout,
  showInventory,
}: {
  email: string;
  role: AppRole;
  onLogout: () => void;
  showInventory: boolean;
}) {
  const { pathname } = useLocation();
  // The last level is where you are; everything before it is the way down to it.
  // A page named like its group ("Tasks / Tasks") shows the name once.
  const levels = [...crumbFor(pathname), ...docTrail(pathname)].filter((l, i, a) => i === 0 || l !== a[i - 1]);
  const tail = useCrumbTail();

  return (
    <header className="topbar">
      <Link to="/" className="brand-link" aria-label="OptInfra home">
        <Logo />
      </Link>
      <nav className="crumb" aria-label="Breadcrumb">
        {levels.map((label, i) => (
          <Fragment key={`${i}-${label}`}>
            {i > 0 && <span className="crumb-sep">/</span>}
            <span className={i === levels.length - 1 ? 'crumb-page' : 'crumb-group'}>{label}</span>
          </Fragment>
        ))}
        {tail && (
          <>
            <span className="crumb-sep">/</span>
            <span className="crumb-tail">{tail}</span>
          </>
        )}
      </nav>
      <div className="spacer" />
      {showInventory && <GlobalSearchBar />}
      <AccountMenu email={email} role={role} onLogout={onLogout} />
    </header>
  );
}
